import { columnType } from "./columnValues";
import type { ColumnType, DataTableColDef } from "./types";

// Floors for columns that set no width or flex.
const TYPE_MIN_WIDTH: Record<ColumnType, number> = {
  string: 120,
  number: 110,
  date: 115,
  singleSelect: 140,
  boolean: 100,
  actions: 56,
};

// MUI's default, so sized columns keep the widths they had on the old grid.
const SIZED_MIN_WIDTH = 50;

export const MAX_AUTOFIT_WIDTH = 600;

export function minWidthOf<R>(column: DataTableColDef<R>): number {
  if (column.minWidth !== undefined) return column.minWidth;
  return column.flex || column.width
    ? SIZED_MIN_WIDTH
    : TYPE_MIN_WIDTH[columnType(column)];
}

// Fixed and user widths stay; flex columns share the rest, never below their floor.
export function computeWidths<R>(
  columns: DataTableColDef<R>[],
  containerWidth: number,
  userWidths: Record<string, number>,
): Record<string, number> {
  const widths: Record<string, number> = {};
  let used = 0;
  let pool: DataTableColDef<R>[] = [];

  columns.forEach((column) => {
    const userWidth = userWidths[column.field];
    if (userWidth !== undefined) {
      widths[column.field] = userWidth;
      used += userWidth;
    } else if (!column.flex) {
      const width = Math.max(column.width ?? 0, minWidthOf(column));
      widths[column.field] = width;
      used += width;
    } else {
      pool.push(column);
    }
  });

  let free = Math.max(containerWidth - used, 0);
  let clamped = true;
  while (clamped && pool.length) {
    clamped = false;
    const available = free;
    const flexTotal = pool.reduce((sum, column) => sum + (column.flex ?? 0), 0);
    const tooNarrow = pool.find(
      (column) =>
        (available * (column.flex ?? 0)) / flexTotal < minWidthOf(column),
    );
    if (tooNarrow) {
      widths[tooNarrow.field] = minWidthOf(tooNarrow);
      free = Math.max(free - minWidthOf(tooNarrow), 0);
      pool = pool.filter((column) => column !== tooNarrow);
      clamped = true;
    }
  }

  const flexTotal = pool.reduce((sum, column) => sum + (column.flex ?? 0), 0);
  let remaining = free;
  pool.forEach((column, index) => {
    const share =
      index === pool.length - 1
        ? remaining
        : Math.floor((free * (column.flex ?? 0)) / flexTotal);
    widths[column.field] = share;
    remaining -= share;
  });

  return widths;
}
